import React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { CreateProjectForm } from '@/console/features/projects/components/create-project/create-project-form';
import {
  TCreateProjectFormProps,
  TCreateProjectInputSchema,
} from '@/console/features/projects/components/create-project/create-project.types';
import { useCreateProject } from '@/console/features/projects/hooks/use-create-project/use-create-project';
import { Submit } from '@/shared/components/ui/form/components/submit/submit';
import { Form } from '@/shared/components/ui/form/form';
import { FormContainer } from '@/shared/components/ui/form/form.styled';
import { TSubmittedData } from '@/shared/components/ui/form/form.types';
import { ProjectTypeEnum } from '@/shared/types/types.generated';
import { showToastError } from '@/shared/utils/toast';

import { createProjectInputSchema } from './create-project.validation';

export const CreateProject: React.FC<TCreateProjectFormProps> = ({
  groupId,
  handleClose,
}) => {
  const { t } = useTranslation();
  const { createProject, uploadPercentage: progress } = useCreateProject();

  const onSubmit = async (data: TSubmittedData<TCreateProjectInputSchema>) => {
    try {
      const { vaultId: _, ...project } = data.data;
      await createProject({ groupId: project.groupId ?? groupId, ...project });
      toast.success(t('projects:form.success.create_project'));
      handleClose();
    } catch {
      showToastError(t('projects:form.error.create_project'));
    }
  };

  const getLoaderText = () => {
    const loaderText = t('projects.form.modal_description.create_project');
    return progress > 0
      ? `${loaderText} ${t('uploading_file_progress', { progress })}`
      : loaderText;
  };

  return (
    <Form<TCreateProjectInputSchema>
      onSubmit={onSubmit}
      schema={createProjectInputSchema}
      loaderText={getLoaderText()}
      defaultValues={{
        projectType: ProjectTypeEnum.Standard,
        groupId,
      }}
    >
      {() => (
        <FormContainer justifyContent="space-between">
          <CreateProjectForm groupId={groupId} />
          <Submit onClose={handleClose} />
        </FormContainer>
      )}
    </Form>
  );
};
